import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { mensagemSenhaRepetida } from '@/lib/senhaFilaErro';
import { formatarDinheiro } from '@/pages/gerenciar/utils';
import Card from '@/components/ui/Card';
import FormRegistrarPagamento, {
  type PagamentoPayload,
} from '@/pages/gerenciar/components/FormRegistrarPagamento';
import {
  carregarFilaPorSenha,
  corrigirSenhaFila,
  cancelarNaoValidadas,
  type FilaPorSenha as Fila,
  type ItemFila,
  type SituacaoItemFila,
} from '@/lib/presencial';
import FolhaCorrigirSenha from './FolhaCorrigirSenha';
import FolhaEncerrar from './FolhaEncerrar';

const INTERVALO_MS = 15_000;

const ALERTA_LABEL: Record<string, string> = {
  senha_repetida: 'Senha repetida',
  senha_invalida: 'Senha inválida',
  senha_de_outro_grupo: 'Senha de outro grupo',
};

const SECAO: Record<SituacaoItemFila, string> = {
  vaga: 'Vagas',
  suplente: 'Suplentes',
  aguardando: 'Ficha em andamento',
};

// Compara senha/nome sem ligar para maiúsculas nem acentos ("37", "m37" e
// "M37" encontram M37).
function normaliza(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

interface Props {
  modalidadeId: number;
  modalidadeNome: string;
}

// Fila pela senha: a direção acompanha a ordem da senha (vagas, suplentes e
// fichas em andamento), registra pagamento e corrige senha. Atualiza a cada 15 s.
export default function FilaPorSenha({ modalidadeId, modalidadeNome }: Props) {
  const [fila, setFila] = useState<Fila | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [grupoAtivo, setGrupoAtivo] = useState<number | null>(null);
  const [busca, setBusca] = useState('');
  const [toast, setToast] = useState<string | null>(null);

  const [pagamentoItem, setPagamentoItem] = useState<ItemFila | null>(null);
  const [salvandoPag, setSalvandoPag] = useState(false);
  const [correcaoItem, setCorrecaoItem] = useState<ItemFila | null>(null);
  const [salvandoSenha, setSalvandoSenha] = useState(false);
  const [erroSenha, setErroSenha] = useState<string | null>(null);
  const [encerrarAberto, setEncerrarAberto] = useState(false);
  const [encerrando, setEncerrando] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const f = await carregarFilaPorSenha(modalidadeId);
      setFila(f);
      setErro(null);
    } catch (e) {
      setErro(mensagemAmigavel(e, 'Não foi possível carregar a fila.'));
    } finally {
      setCarregando(false);
    }
  }, [modalidadeId]);

  useEffect(() => {
    setCarregando(true);
    setGrupoAtivo(null);
    setBusca('');
    carregar();
    const timer = setInterval(carregar, INTERVALO_MS);
    return () => clearInterval(timer);
  }, [carregar]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // Puxar do topo para atualizar (mobile).
  const inicioY = useRef<number | null>(null);
  const puxandoRef = useRef(0);
  const [puxando, setPuxando] = useState(0);
  useEffect(() => {
    function onStart(e: TouchEvent) {
      inicioY.current = window.scrollY <= 0 ? e.touches[0].clientY : null;
    }
    function onMove(e: TouchEvent) {
      if (inicioY.current == null) return;
      const dy = e.touches[0].clientY - inicioY.current;
      const val = dy > 0 ? Math.min(dy, 80) : 0;
      puxandoRef.current = val;
      setPuxando(val);
    }
    function onEnd() {
      if (puxandoRef.current > 60) carregar();
      puxandoRef.current = 0;
      setPuxando(0);
      inicioY.current = null;
    }
    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onEnd);
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
    };
  }, [carregar]);

  const grupos = fila?.grupos ?? [];
  const grupo = grupos.find((g) => g.id === grupoAtivo) ?? grupos[0] ?? null;
  const buscaLimpa = normaliza(busca);
  const itensFiltrados = grupo
    ? grupo.itens.filter((it) => {
        if (!buscaLimpa) return true;
        return (
          normaliza(it.senha ?? '').includes(buscaLimpa) ||
          normaliza(it.nome ?? '').includes(buscaLimpa)
        );
      })
    : [];

  const secoes: { titulo: string; itens: ItemFila[] }[] = [];
  if (grupo) {
    for (const it of itensFiltrados) {
      const titulo = SECAO[it.situacao] ?? 'Fila';
      const ultima = secoes[secoes.length - 1];
      if (ultima && ultima.titulo === titulo) ultima.itens.push(it);
      else secoes.push({ titulo, itens: [it] });
    }
  }

  async function registrarPagamento(payload: PagamentoPayload) {
    if (!pagamentoItem) return;
    setSalvandoPag(true);
    try {
      const { error } = await supabase.rpc('registrar_pagamento', {
        p_inscricao: pagamentoItem.inscricao_id,
        p_valor_reais: payload.valor_centavos / 100,
        p_metodo: payload.metodo,
        p_observacao: payload.observacao,
        p_pago_em: payload.pago_em,
      });
      if (error) throw error;
      setPagamentoItem(null);
      setToast('Pagamento registrado.');
      carregar();
    } catch (e) {
      setToast(mensagemAmigavel(e, 'Não foi possível registrar o pagamento.'));
    } finally {
      setSalvandoPag(false);
    }
  }

  async function salvarSenha(novaSenha: string) {
    if (!correcaoItem) return;
    setSalvandoSenha(true);
    setErroSenha(null);
    try {
      await corrigirSenhaFila(correcaoItem.inscricao_id, novaSenha);
      setCorrecaoItem(null);
      setToast('Senha corrigida.');
      carregar();
    } catch (e) {
      setErroSenha(
        mensagemSenhaRepetida(e) ?? mensagemAmigavel(e, 'Não foi possível corrigir a senha.'),
      );
    } finally {
      setSalvandoSenha(false);
    }
  }

  async function encerrar() {
    setEncerrando(true);
    try {
      const r = await cancelarNaoValidadas(modalidadeId);
      setEncerrarAberto(false);
      setToast(`${r.canceladas} fichas canceladas · ${r.promovidos} suplentes subiram para vaga`);
      carregar();
    } catch (e) {
      setToast(mensagemAmigavel(e, 'Não foi possível encerrar agora.'));
    } finally {
      setEncerrando(false);
    }
  }

  if (carregando) {
    return (
      <div className="space-y-3">
        <div className="h-11 animate-pulse rounded-[16px] bg-background-200/70" />
        <div className="h-64 animate-pulse rounded-[18px] bg-background-200/70" />
      </div>
    );
  }

  if (erro && !fila) {
    return (
      <Card className="p-8 text-center">
        <p className="text-[15px] text-foreground-700">{erro}</p>
        <button
          type="button"
          onClick={carregar}
          className="aperta mt-4 cursor-pointer whitespace-nowrap rounded-[16px] bg-primary-500 px-5 py-2.5 text-[14px] font-semibold text-white transition-colors hover:bg-primary-600"
        >
          Tentar de novo
        </button>
      </Card>
    );
  }

  if (grupos.length === 0) {
    return (
      <Card className="p-8 text-center">
        <span className="mx-auto flex h-12 w-12 items-center justify-center text-foreground-300">
          <i className="ri-list-ordered text-[34px]" aria-hidden />
        </span>
        <p className="mt-3 text-[15px] text-foreground-600">Ainda não há ninguém nesta fila.</p>
      </Card>
    );
  }

  return (
    <div>
      {puxando > 0 && (
        <div
          className="flex items-center justify-center text-[13px] text-foreground-400"
          style={{ height: puxando }}
        >
          {puxando > 60 ? 'Solte para atualizar' : 'Puxe para atualizar'}
        </div>
      )}

      <p className="rounded-[14px] bg-accent-50 px-4 py-2.5 text-[13px] font-semibold text-accent-900">
        A ordem é a da senha da fila.
      </p>

      {/* Troca de grupo */}
      {grupos.length > 1 && (
        <div className="-mx-4 mt-3 overflow-x-auto px-4">
          <div className="flex gap-1 rounded-full bg-background-100 p-1">
            {grupos.map((g) => (
              <button
                key={g.id}
                type="button"
                onClick={() => setGrupoAtivo(g.id)}
                className={`flex-1 cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-[14px] font-semibold transition-colors ${
                  grupo?.id === g.id
                    ? 'bg-secondary-500 text-white'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                {g.nome}
              </button>
            ))}
          </div>
        </div>
      )}

      {grupo && (
        <div className="relative mt-3">
          <span className="pointer-events-none absolute left-4 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-search-line text-base" aria-hidden />
          </span>
          <input
            type="text"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por senha ou nome"
            autoComplete="off"
            className="h-11 w-full rounded-full border border-background-200 bg-background-50 pl-11 pr-11 text-[15px] text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
          {busca && (
            <button
              type="button"
              onClick={() => setBusca('')}
              aria-label="Limpar busca"
              className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full text-foreground-400 transition-colors hover:bg-background-100 hover:text-foreground-700"
            >
              <i className="ri-close-line text-lg" aria-hidden />
            </button>
          )}
        </div>
      )}

      {grupo && (
        <p className="mt-3 text-[13px] text-foreground-600">
          Vagas <strong className="font-semibold text-foreground-900">{grupo.com_vaga}</strong>
          {grupo.vagas != null ? `/${grupo.vagas}` : ''} · Pagos{' '}
          <strong className="font-semibold text-foreground-900">{grupo.pagos}</strong> · Suplentes{' '}
          <strong className="font-semibold text-foreground-900">{grupo.suplentes}</strong> · Ficha em
          andamento{' '}
          <strong className="font-semibold text-foreground-900">{grupo.aguardando}</strong>
        </p>
      )}

      <div className="mt-3 space-y-4">
        {secoes.length === 0 && buscaLimpa && (
          <p className="py-6 text-center text-[14px] text-foreground-500">
            Ninguém encontrado para “{busca.trim()}”.
          </p>
        )}
        {secoes.map((s) => (
          <div key={s.titulo}>
            <h3 className="px-1 text-[13px] font-bold uppercase tracking-wide text-foreground-500">
              {s.titulo} · {s.itens.length}
            </h3>
            <div className="mt-2 space-y-2">
              {s.itens.map((it) => (
                <LinhaFila
                  key={it.inscricao_id}
                  item={it}
                  aoPagar={setPagamentoItem}
                  aoCorrigir={(item) => {
                    setErroSenha(null);
                    setCorrecaoItem(item);
                  }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={() => setEncerrarAberto(true)}
        className="aperta mt-6 flex h-12 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[16px] border border-danger-300 bg-background-50 text-[15px] font-semibold text-danger-600 transition-colors hover:bg-danger-50"
      >
        <i className="ri-lock-line text-lg" aria-hidden />
        Encerrar inscrições
      </button>

      {pagamentoItem && (
        <FormRegistrarPagamento
          aoSalvar={registrarPagamento}
          aoCancelar={() => setPagamentoItem(null)}
          salvando={salvandoPag}
          valorInicialCentavos={pagamentoItem.falta ?? 0}
          ajudaValor={`Quanto foi recebido. ${pagamentoItem.nome}, senha ${pagamentoItem.senha ?? '—'}.`}
          avisoTopo={pagamentoItem.situacao === 'suplente' ? 'Suplente: ainda sem vaga.' : null}
        />
      )}

      <FolhaCorrigirSenha
        aberto={!!correcaoItem}
        nome={correcaoItem?.nome ?? ''}
        senhaAtual={correcaoItem?.senha ?? null}
        salvando={salvandoSenha}
        erro={erroSenha}
        aoSalvar={salvarSenha}
        aoFechar={() => setCorrecaoItem(null)}
      />

      <FolhaEncerrar
        aberto={encerrarAberto}
        modalidadeNome={modalidadeNome}
        encerrando={encerrando}
        aoConfirmar={encerrar}
        aoFechar={() => setEncerrarAberto(false)}
      />

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-[90] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 rounded-2xl bg-foreground-950 px-4 py-3 text-center text-[14px] text-white">
          {toast}
        </div>
      )}
    </div>
  );
}

interface LinhaProps {
  item: ItemFila;
  aoPagar: (item: ItemFila) => void;
  aoCorrigir: (item: ItemFila) => void;
}

function LinhaFila({ item, aoPagar, aoCorrigir }: LinhaProps) {
  const pago = item.falta != null && item.falta <= 0;
  return (
    <div className="rounded-[16px] border border-background-200 bg-background-50 p-3">
      <div className="flex items-start gap-3">
        <span className="flex h-11 min-w-[54px] shrink-0 items-center justify-center rounded-[12px] bg-foreground-950 px-2 font-mono text-[18px] font-extrabold text-white">
          {item.senha ?? '—'}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-semibold leading-snug text-foreground-950">{item.nome}</p>
          <p className="mt-0.5 text-[13px] text-foreground-500">
            {item.idade != null ? `${item.idade} anos` : 'Idade não informada'}
          </p>
          <span
            className={`mt-1.5 inline-flex rounded-full px-3 py-1 text-[13px] font-bold ${
              pago ? 'bg-success-100 text-success-700' : 'bg-warning-100 text-warning-800'
            }`}
          >
            {pago ? 'Pago' : item.falta != null ? `Falta ${formatarDinheiro(item.falta)}` : '—'}
          </span>
        </div>
      </div>

      {item.alertas.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {item.alertas.map((a) => (
            <span
              key={a}
              className="rounded-full bg-danger-100 px-2.5 py-0.5 text-[12px] font-semibold text-danger-700"
            >
              {ALERTA_LABEL[a] ?? a}
            </span>
          ))}
        </div>
      )}

      <div className="mt-2.5 flex gap-2">
        <button
          type="button"
          onClick={() => aoPagar(item)}
          className="aperta flex h-10 flex-1 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-[14px] bg-primary-500 text-[14px] font-semibold text-white transition-colors hover:bg-primary-600"
        >
          <i className="ri-money-dollar-circle-line text-base" aria-hidden />
          Registrar pagamento
        </button>
        <button
          type="button"
          onClick={() => aoCorrigir(item)}
          className="aperta flex h-10 flex-1 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-[14px] border border-background-300 bg-background-50 text-[14px] font-semibold text-foreground-800 transition-colors hover:bg-background-100"
        >
          <i className="ri-edit-line text-base" aria-hidden />
          Corrigir senha
        </button>
      </div>
    </div>
  );
}